import { Fragment, useEffect, useRef, useState } from 'react';
import nl2br from 'react-newline-to-break';
import './EditablePreview.scss';
import VisualisedNode, { IMAGE_MODES, WORKING_ON_NODE_DATA } from './VisualisedComponents/VisualisedNode';
import { CUSTOM_COMPONENTS_LIST } from './VisualisedComponents/CustomComponentsList';
import { evaluateDisplayConditionsForField } from '../ModuleList/customFields/CustomFieldRenderer';
import { triggerSaveFeatureData, undoRedoHook } from '../../v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions';

// Get group data from customisationVars, this is one level higher than getCustomisationValue,
// but actually works in exactly the same way, except that getCustomisationValue supports some post processing of the value.
// Pass in -1 as the index to get the entire array.
export const getGroupData = (customisationVars, path, index = 0, forceGroup = false) => {
    if (!customisationVars || !path) return null;
    const keys = path.split('.');
    if (forceGroup && keys.length === 4) {
        // Remove the last key to get the group data
        keys.pop();
    }
    let value = customisationVars;
    for (const key of keys) {
        if (Array.isArray(value[key])) {
            if (index >= 0 && index < value[key].length) {
                value = value[key][index]; // Use index if value is an array
            } else {
                value = value[key]; // Return the array as is if index is not specified or out of bounds
                return value;
            }
        } else
        if (value && key in value) {
            value = value[key];
        } else {
            console.warn(`getGroupData Path ${path} not found in customisationVars, returning null.`);
            return null; // Path not found
        }
    }
    // console.log("getGroupData for path:", path, "value:", value);
    return value;
}

export const getCustomisationFieldData = (campaignData, featureId, path/*, index = 0, forceComponentVarKey = null*/) => {
    if (!campaignData || !featureId || !path) return null;
    let featureData = null;
    for (let i = 0; i < campaignData.features.length; i++) {
        if (campaignData.features[i]._id === featureId) {
            featureData = campaignData.features[i];
            break;
        }
    }
    if (!featureData) return null;
    const featureCustomisation = featureData?.feature?.customisation;
    if (!featureCustomisation) return null;
    const pathSplit = path.split('.');

    // Find the tab in the feature customisation
    let tabData = null;
    for (let i = 0; i < featureCustomisation.length; i++) {
        if (featureCustomisation[i].tabKey === pathSplit[0]) {
            tabData = featureCustomisation[i];
            break;
        }
    }
    if (!tabData) return null;
    if (pathSplit.length < 2) {
        // If the path only contains the tab key, return the tab data
        return tabData;
    }

    // Find the group in the tab
    let groupData = null;
    for (let i = 0; i < tabData.options.length; i++) {
        if (tabData.options[i].varKey === pathSplit[1]) {
            groupData = tabData.options[i];
            break;
        }
    }
    if (!groupData) return null;
    if (pathSplit.length < 3) {
        // If the path only contains the tab key and group key, return the group data
        return groupData;
    }

    // Find the option in the group
    let optionData = null;
    for (let i = 0; i < groupData.variables.length; i++) {
        if (groupData.variables[i].varName === pathSplit[2]) {
            optionData = groupData.variables[i];
            break;
        }
    }
    if (!optionData) return null;
    if (pathSplit.length < 4) {
        // If the path only contains the tab key, group key and option key, return the option data
        return optionData;
    }

    // Last one is the actual var schema
    if (optionData?.valSchema?.[pathSplit[3]]) {
        return optionData.valSchema[pathSplit[3]];
    }

    return optionData;
}

export const getCustomisationValue = (customisationVars, path, index = 0, forceComponentVarKey = null, node = null) => {
    if (!customisationVars || !path || !path.split) return null;
    // console.log("Getting customisation value for path:", path, "index:", index, "forceComponentVarKey:", forceComponentVarKey);
    const keys = path.split('.');
    let value = customisationVars;
    for (const key of keys) {
        if (Array.isArray(value[key])) {
            if (value[key].length > 0) {
                if (index >= 0 && index < value[key].length) {
                    value = value[key][index]; // Use index if value is an array
                } else {
                    // If this is our final key, set the value to the array, otherwise set the value to the first element
                    if (key === keys[keys.length - 1]) {
                        value = value[key];
                    } else {
                        value = value[key][0];
                    }
                }
            } else {
                return null; // Empty array
            }
        } else
        if (value && key in value) {
            value = value[key];
        } else {
            return null; // Path not found
        }
    }
    if (forceComponentVarKey === null) {
        // See if we have a key in WORKING_ON_NODE_DATA.groupData
        if (WORKING_ON_NODE_DATA.groupData) {
            const keyToUse = WORKING_ON_NODE_DATA.groupData?.visualisationConfig?.visualisationComponent;
            if (keyToUse && keyToUse !== '<Standard>') {
                forceComponentVarKey = keyToUse;
            }
        }
    }
    // console.log("Customisation value for path:", path, "value:", value, "forceComponentVarKey:", forceComponentVarKey);
    if (forceComponentVarKey || (WORKING_ON_NODE_DATA.visualisedNodeVarKey && WORKING_ON_NODE_DATA.fieldData)) {
        // If we are working on a visualised node, we can post-process the value
        const customComponent = CUSTOM_COMPONENTS_LIST[forceComponentVarKey ?? WORKING_ON_NODE_DATA.visualisedNodeVarKey];
        if (customComponent && customComponent.convertStoredValueToVisualisedValue) {
            return customComponent.convertStoredValueToVisualisedValue(WORKING_ON_NODE_DATA.fieldData, value, path, node);
        }
    }
    return value;
}

// visualisationExtraData varies depending on what we are changing, when updating dimensions it is the width or height of the on-screen rectangle before the change, allowing various conversions of units
export const updateCustomisationValue = (customisationVars, path, value, index = 0, forceComponentVarKey = null, node = null, visualisationExtraData = null, valueChangedCallback = null, recordActionForUndo = true, bypassConversion = false, saveToServer = true, updateEvenIfValuesDontChange = false, forcePrevValueTo = null) => {
    console.log("Updating customisationVars at path:", path, "to value:", value, WORKING_ON_NODE_DATA, forceComponentVarKey, typeof value, customisationVars);
    
    if (valueChangedCallback) {
        valueChangedCallback(value);
    }

    if (!customisationVars || !path) return false;
    const keys = path.split('.');
    let current = customisationVars;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (Array.isArray(current[key])) {
            current = current[key][index]; // Use index if value is an array
        } else
        if (current && key in current) {
            current = current[key];
        } else {
            console.warn(`Path ${path} not found in customisationVars, cannot update value.`);
            return false; // Path not found
        }
    }
    const lastKey = keys[keys.length - 1];
    let valueToStore = value;
    if (bypassConversion === false) {
        if (forceComponentVarKey || (WORKING_ON_NODE_DATA.visualisedNodeVarKey && WORKING_ON_NODE_DATA.fieldData)) {
            // If we are working on a visualised node, we can post-process the value
            const customComponent = CUSTOM_COMPONENTS_LIST[forceComponentVarKey || WORKING_ON_NODE_DATA.visualisedNodeVarKey];
            if (customComponent && customComponent.convertVisualisedValueToStoredValue) {
                valueToStore = customComponent.convertVisualisedValueToStoredValue(WORKING_ON_NODE_DATA.fieldData, value, path, node, visualisationExtraData);
            }
        }
    }
    
    // If the current value is a boolean, we need to convert the new value to the appropriate type
    console.log('Updating customisationVars: Are we updating a boolean?', typeof current?.[lastKey], 'with value:', valueToStore, typeof valueToStore);
    if (current === undefined || current === null) {
        console.warn(`Path ${path} resolved to null/undefined parent, cannot update value.`);
        return false;
    }
    if (typeof current?.[lastKey] === 'boolean') {
        if (typeof value === 'string') {
            valueToStore = value.toLowerCase() === 'true';
        }
        if (typeof value === 'number') {
            valueToStore = value > 0; // Treat <= 0 as false, anything else as true
        }
    }
    
    const prevValue = current[lastKey];
    current[lastKey] = valueToStore; // Update the last key with the new value
    console.log(`Updating customisationVars: Updated customisationVars at path ${path} to value:`, value, valueToStore);
    console.log(current);

    // Trigger save
    console.log('Triggering save for node:', node, 'with customisationVars:', customisationVars);
    if (saveToServer && node && customisationVars && (updateEvenIfValuesDontChange || prevValue !== valueToStore || (forcePrevValueTo && prevValue !== forcePrevValueTo))) {
        triggerSaveFeatureData(node?.featureData?._id, customisationVars, node?.featureData);
    }

    if (recordActionForUndo && (updateEvenIfValuesDontChange || prevValue !== valueToStore || (forcePrevValueTo && prevValue !== forcePrevValueTo))) {
        // Record the action for undo system
        undoRedoHook?.recordCustomisationValueChange?.(
            node?.featureData?._id,
            node,
            path,
            forcePrevValueTo ?? prevValue,
            valueToStore,
            index,
            forceComponentVarKey || WORKING_ON_NODE_DATA.visualisedNodeVarKey,
            visualisationExtraData
        );
    }

    return true; // Successfully updated
}

export const canNodeBeMovedOrResized = (node, customisationVars, campaignData, selectedFeatureNode, visualisationComponentKey, index) => {
    if (!node || !node.config || !node.config.visualisationConfig) {
        return { moveable: false, resizable: false, widthScaleable: false, heightScaleable: false };
    }
    let moveable = false;
    let resizable = false;
    let widthScaleable = false;
    let heightScaleable = false;
    // Moving is determined by the existence of a position module
    // Resizing is determined by the existence of a dimensions module
    if (node.config.children && node.config.children.length > 0) {
        for (const child of node.config.children) {
            if (child.type === 'module') {
                const fakePropsForDisplayConditions = {
                    customisationVars: customisationVars,
                    campaignData: campaignData,
                    selectedFeatureNode: selectedFeatureNode,
                    visualisationComponentKey: visualisationComponentKey,
                    index: index,
                }
                const canCheckDisplayConditions = customisationVars && campaignData && selectedFeatureNode && visualisationComponentKey !== undefined && index !== undefined;
                if (node.draggable !== false && child.module === 'position') {
                    // This only counts as draggable if we have an offset-x or offset-y field with a dataLink
                    // ---NOTE: switched to requiring both, this is only temporary until the proper solution is worked out,
                    // the problem was that when dragging elements on the wave's progress bar (center image and lock image)
                    // they didn't behave quite as you would expect, but the full implementation is not yet complete
                    if (/* child.moduleSettings.visualise &&  */child.fields.some(field => field['offset-x']?.dataLink && field['offset-y']?.dataLink)) {
                        moveable = true;
                    }
                } else if (child.module === 'dimensions') {
                    // This only counts as resizable if we have a dimensions-height or dimensions-width field with a dataLink
                    if (child.fields.some(field => field['dimensions-height']?.dataLink || field['dimensions-width']?.dataLink)) {
                        resizable = true;
                        // Can we sale both the width and height?
                        if (child.fields.some(field => field['dimensions-width']?.dataLink) && (!node.overrideScaleable || node.overrideScaleable.width !== false)) {
                            const field = child.fields.find(f => f['dimensions-width']?.dataLink);
                            const canDisplay = canCheckDisplayConditions ? evaluateDisplayConditionsForField(fakePropsForDisplayConditions, field?.["dimensions-width"]) : true;
                            if (canDisplay) {
                                widthScaleable = true;
                            }
                        }
                        if (child.fields.some(field => field['dimensions-height']?.dataLink) && (!node.overrideScaleable || node.overrideScaleable.height !== false)) {
                            const field = child.fields.find(f => f['dimensions-height']?.dataLink);
                            const canDisplay = canCheckDisplayConditions ? evaluateDisplayConditionsForField(fakePropsForDisplayConditions, field?.["dimensions-height"]) : true;
                            if (canDisplay) {
                                heightScaleable = true;
                            }
                        }
                        if (!widthScaleable && !heightScaleable) {
                            resizable = false;
                        }
                    }
                }
            }
        }
    }
    return { moveable, resizable, widthScaleable, heightScaleable };
}

export const findCustomRendererKey = (node) => {
    // console.log(">>>>> Finding custom renderer key for node:", node);
    let key = null;
    if (node?.config?.visualisationConfig?.visualisationComponent && node.config.visualisationConfig.visualisationComponent !== '<Standard>') {
        key = node.config.visualisationConfig.visualisationComponent;
    } else {
        let parentNode = node?.parent;
        while (parentNode) {
            if (parentNode?.config?.visualisationConfig?.visualisationComponent && parentNode.config.visualisationConfig.visualisationComponent !== '<Standard>') {
                key = parentNode.config.visualisationConfig.visualisationComponent;
                break;
            }
            parentNode = parentNode.parent;
        }
    }
    // console.log("<<<<< Found custom renderer key:", key);
    return key;
}

export const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    if (typeof string !== 'string') return string;
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export const findSubNode = (node, searchString) => {
    // console.log("Finding subnode in node:", node, "for searchString:", searchString);
    if (!node || !node.subNodes || node.subNodes.length === 0 || !searchString) return null;
    const normalizedSearch = typeof searchString === 'string' ? searchString.toLowerCase() : null;

    for (const subNode of node.subNodes) {
        if (subNode.id === searchString || subNode.name === searchString || subNode.path === searchString) {
            return subNode;
        }

        const id = typeof subNode.id === 'string' ? subNode.id : null;
        const name = typeof subNode.name === 'string' ? subNode.name : null;
        const path = typeof subNode.path === 'string' ? subNode.path : null;

        if ((id && id.includes(searchString)) || (name && name.includes(searchString)) || (path && path.includes(searchString))) {
            return subNode;
        }

        if (
            normalizedSearch && (
                (id && id.toLowerCase().includes(normalizedSearch)) ||
                (name && name.toLowerCase().includes(normalizedSearch)) ||
                (path && path.toLowerCase().includes(normalizedSearch))
            )
        ) {
            return subNode;
        }
    }
    // Look for nodes in subNodes recursively
    for (const subNode of node.subNodes) {
        const foundNode = findSubNode(subNode, searchString);
        if (foundNode) {
            return foundNode;
        }
    }
    return null;
}

const EditablePreview = (props) => {
    // console.log("EditablePreview component props:", props);

    const campaignData = props.campaignData;

    const [previewBg, setPreviewBg] = useState(Math.random() > 0.5 ? '1' : '2');
    const [previewScale, setPreviewScale] = useState(1);
    const previewScaleRef = useRef(null);
    const calculatePreviewScale = () => {
        const previewWindow = document.getElementById('previewWindow');
        const previewWindowContainer = document.getElementById('previewWindowContainer');
        if (previewWindow && previewWindowContainer) {
            const height = previewWindowContainer.offsetHeight;
            const width = height * 16 / 9;
            previewWindow.style.width = `${width}px`;
            previewWindow.style.height = `${height}px`;
            // console.log("Preview window dimensions set to:", width, height);
            // Set the scale based on the width
            const scale = width / 1920; // Assuming 1920 is the base width
            setPreviewScale(scale);
            previewScaleRef.current = scale;
            // console.log("Preview scale set to:", scale);
            invalidateHighlightedNodeStates();
        }
    }

    useEffect(() => {
        calculatePreviewScale();
        window.addEventListener('resize', calculatePreviewScale);
        return () => window.removeEventListener('resize', calculatePreviewScale);
    }, []);
    useEffect(() => {
        calculatePreviewScale();
    }, [props.timelineHeight]);

    const [highlightedNodeDomElement, setHighlightedNodeDomElement] = useState(null);
    const highlightedNodeDomElementRef = useRef(highlightedNodeDomElement);
    const [highlightedDomNodeRect, setHighlightedDomNodeRect] = useState(null);
    const highlightedDomNodeRectRef = useRef(highlightedDomNodeRect);
    const [highlightedNodeIsMoveable, setHighlightedNodeIsMoveable] = useState(null);
    const highlightedNodeIsMoveableRef = useRef(highlightedNodeIsMoveable);
    const [highlightedNodeIsResizable, setHighlightedNodeIsResizable] = useState(null);
    const highlightedNodeIsResizableRef = useRef(highlightedNodeIsResizable);
    const [highlightedNodeIsWidthScaleable, setHighlightedNodeIsWidthScaleable] = useState(null);
    const highlightedNodeIsWidthScaleableRef = useRef(highlightedNodeIsWidthScaleable);
    const [highlightedNodeIsHeightScaleable, setHighlightedNodeIsHeightScaleable] = useState(null);
    const highlightedNodeIsHeightScaleableRef = useRef(highlightedNodeIsHeightScaleable);
    const [forceProportionalScaling, setForceProportionalScaling] = useState(false);
    const forceProportionalScalingRef = useRef(forceProportionalScaling);

    const [hoverHighlightedDomNodeRect, setHoverHighlightedDomNodeRect] = useState(null);
    const hoverHighlightedDomNodeRectRef = useRef(hoverHighlightedDomNodeRect);
    useEffect(() => {
        highlightedNodeDomElementRef.current = highlightedNodeDomElement;
        highlightedDomNodeRectRef.current = highlightedDomNodeRect;
        highlightedNodeIsMoveableRef.current = highlightedNodeIsMoveable;
        highlightedNodeIsResizableRef.current = highlightedNodeIsResizable;
        highlightedNodeIsWidthScaleableRef.current = highlightedNodeIsWidthScaleable;
        highlightedNodeIsHeightScaleableRef.current = highlightedNodeIsHeightScaleable;
        hoverHighlightedDomNodeRectRef.current = hoverHighlightedDomNodeRect;
        forceProportionalScalingRef.current = forceProportionalScaling;
    }, [highlightedNodeDomElement, highlightedDomNodeRect, highlightedNodeIsMoveable, highlightedNodeIsResizable, highlightedNodeIsWidthScaleable, highlightedNodeIsHeightScaleable, hoverHighlightedDomNodeRect, forceProportionalScaling]);
    // This is used to force a re-render of the highlighted node states, every time we increment it the highlighted node states will be recalculated
    const [highlightedNodeStatesInvalidated, setHighlightedNodeStatesInvalidated] = useState(0);

    const findDomNodeTimeoutId = useRef(null);
    const setHighlightedNodeStates = (node, moveable, resizable, widthScaleable, heightScaleable, autoRecalculate = false) => {
        if (findDomNodeTimeoutId.current) {
            clearTimeout(findDomNodeTimeoutId.current);
        }
        if (node?.config?.groupSettings?.visualise === false) {
            // No bounding box for non-visualised groups
            // console.log("Node has visualise set to false, not setting highlighted node states for node:", node);
            clearHighlightedHoverNodeState();
            return;
        }
        // console.log("Setting highlighted node states for node:", node, "moveable:", moveable, "resizable:", resizable, "widthScaleable:", widthScaleable, "heightScaleable:", heightScaleable, "autoRecalculate:", autoRecalculate);
        // Find the dom element with the id of the node
        const nodeDomElement = document.getElementById(node?.id);
        const previewWindow = document.getElementById('previewWindow');
        // If we find it, we set the highlightedNodeDomElement state
        if (previewWindow && nodeDomElement) {
            const previewWindowRect = previewWindow.getBoundingClientRect();
            // Get the bounding rectangle of the node
            const rect = nodeDomElement.getBoundingClientRect();
            // Create a new rect that is relative to the preview window
            const relativeRect = {
                left: (rect.left - previewWindowRect.left) - 2,
                top: (rect.top - previewWindowRect.top) - 2,
                width: rect.width + 4,
                height: rect.height + 4
            };

            if (node.boundingBoxOverflowLeft || node.boundingBoxOverflowRight || node.boundingBoxOverflowTop || node.boundingBoxOverflowBottom) {
                // Adjust the left and width to account for the overflow
                if (node.boundingBoxOverflowLeft) {
                    relativeRect.left -= node.boundingBoxOverflowLeft;
                    relativeRect.width += node.boundingBoxOverflowLeft;
                }
                if (node.boundingBoxOverflowRight) {
                    relativeRect.width += node.boundingBoxOverflowRight;
                }
                if (node.boundingBoxOverflowTop) {
                    relativeRect.top -= node.boundingBoxOverflowTop;
                    relativeRect.height += node.boundingBoxOverflowTop;
                }
                if (node.boundingBoxOverflowBottom) {
                    relativeRect.height += node.boundingBoxOverflowBottom;
                }
            } else if (node.boundingBoxOverflow) {
                // Adjust all sides equally
                relativeRect.left -= node.boundingBoxOverflow;
                relativeRect.top -= node.boundingBoxOverflow;
                relativeRect.width += node.boundingBoxOverflow * 2;
                relativeRect.height += node.boundingBoxOverflow * 2;
            }

            let allowSetting = true;
            if (autoRecalculate) {
                // We need to check if the rectangle is the same as the currently highlighted node
                if (highlightedDomNodeRectRef.current &&
                    highlightedDomNodeRectRef.current.left === relativeRect.left &&
                    highlightedDomNodeRectRef.current.top === relativeRect.top &&
                    highlightedDomNodeRectRef.current.width === relativeRect.width &&
                    highlightedDomNodeRectRef.current.height === relativeRect.height) {
                    // If it is the same, we do not set the state again
                    allowSetting = false;
                }

                const nodeMoveableOrResizable = canNodeBeMovedOrResized(node, node.customisationVars, campaignData, selectedFeatureNode, findCustomRendererKey(node), node.dataIndex ?? 0);
                if (moveable !== nodeMoveableOrResizable.moveable ||
                    resizable !== nodeMoveableOrResizable.resizable ||
                    widthScaleable !== nodeMoveableOrResizable.widthScaleable ||
                    heightScaleable !== nodeMoveableOrResizable.heightScaleable) {
                        // If any of these have changed, we allow setting the state again
                        allowSetting = true;
                        moveable = nodeMoveableOrResizable.moveable;
                        resizable = nodeMoveableOrResizable.resizable;
                        widthScaleable = nodeMoveableOrResizable.widthScaleable;
                        heightScaleable = nodeMoveableOrResizable.heightScaleable;
                }
            }

            if (allowSetting) {
                // Set the highlightedNodeDomElement state
                setHighlightedDomNodeRect(relativeRect);
                setHighlightedNodeIsMoveable(moveable);
                setHighlightedNodeIsResizable(resizable);
                setHighlightedNodeIsWidthScaleable(widthScaleable);
                setHighlightedNodeIsHeightScaleable(heightScaleable);
                setForceProportionalScaling(node.forceProportionalScaling ?? false);
                if (!autoRecalculate) {
                    clearHighlightedHoverNodeState();
                }
            }

            if (autoRecalculate) {
                return;
            }
            findDomNodeTimeoutId.current = setTimeout(() => {
                setHighlightedNodeStates(node, moveable, resizable, widthScaleable, heightScaleable, true);
            }, 200); // Sometimes the box size is not correct, so we keep checking it once every 200ms
        } else {
            // If we can't find the node, try again after a short delay
            findDomNodeTimeoutId.current = setTimeout(() => {
                setHighlightedNodeStates(node, moveable, resizable, widthScaleable, heightScaleable);
            }, 100);
        }
    }
    const invalidateHighlightedNodeStates = (forceRerender = false) => {
        setHighlightedNodeDomElement(null);
        setHighlightedDomNodeRect(null);
        clearHighlightedHoverNodeState();
        setHighlightedNodeStatesInvalidated(prev => prev + 1);
        if (!forceRerender) {
            props?.forceUpdateComponent(); // Force update the component to re-render
        }

        // Find any dom elements with the class 'v2-dragging-node-copy' and remove them
        const draggingNodeCopies = document.querySelectorAll('.v2-dragging-node-copy');
        draggingNodeCopies.forEach((copy) => {
            copy.remove();
        });
    }
    useEffect(() => {
        invalidateHighlightedNodeStates(true);
    }, [props.forceUpdate, props.activeNode, props.visualisedNode, props.timelinePlayheadStep]);

    // Used to highlight a node when hovered
    const highlightHoveredNode = (e, node, scaled = false) => {
        console.log("Highlighting hovered node:", node, props.activeNode, scaled);
        e.stopPropagation();
        // Find the dom element with the id of the node
        const nodeDomElement = document.getElementById(node?.id);
        const previewWindow = document.getElementById('previewWindow');
        // If we find it, we set the highlightedNodeDomElement state
        if (previewWindow && nodeDomElement) {
            const previewWindowRect = previewWindow.getBoundingClientRect();
            // Get the bounding rectangle of the node
            const rect = nodeDomElement.getBoundingClientRect();
            // Create a new rect that is relative to the preview window
            const relativeRect = {
                left: ((rect.left + (scaled ? ((rect.width - rect.width * previewScaleRef.current) / 2) : 0)) - previewWindowRect.left) - 2,
                top: ((rect.top + (scaled ? ((rect.height - rect.height * previewScaleRef.current) / 2) : 0)) - previewWindowRect.top) - 2,
                width: rect.width * (scaled ? previewScaleRef.current : 1) + 4,
                height: rect.height * (scaled ? previewScaleRef.current : 1) + 4
            };

            if (node.boundingBoxOverflowLeft || node.boundingBoxOverflowRight || node.boundingBoxOverflowTop || node.boundingBoxOverflowBottom) {
                // Adjust the left and width to account for the overflow
                if (node.boundingBoxOverflowLeft) {
                    relativeRect.left -= node.boundingBoxOverflowLeft;
                    relativeRect.width += node.boundingBoxOverflowLeft;
                }
                if (node.boundingBoxOverflowRight) {
                    relativeRect.width += node.boundingBoxOverflowRight;
                }
                if (node.boundingBoxOverflowTop) {
                    relativeRect.top -= node.boundingBoxOverflowTop;
                    relativeRect.height += node.boundingBoxOverflowTop;
                }
                if (node.boundingBoxOverflowBottom) {
                    relativeRect.height += node.boundingBoxOverflowBottom;
                }
            } else if (node.boundingBoxOverflow) {
                // Adjust all sides equally
                relativeRect.left -= node.boundingBoxOverflow;
                relativeRect.top -= node.boundingBoxOverflow;
                relativeRect.width += node.boundingBoxOverflow * 2;
                relativeRect.height += node.boundingBoxOverflow * 2;
            }

            // Only set this if it is no the same rect as the currently highlighted node
            if (props.activeNode === node ||
                (hoverHighlightedDomNodeRectRef.current &&
                hoverHighlightedDomNodeRectRef.current.left === relativeRect.left &&
                hoverHighlightedDomNodeRectRef.current.top === relativeRect.top &&
                hoverHighlightedDomNodeRectRef.current.width === relativeRect.width &&
                hoverHighlightedDomNodeRectRef.current.height === relativeRect.height)
            ) {
                // If it is the same, we do nothing
                if (props.activeNode === node) {
                    clearHighlightedHoverNodeState();
                }
                return;
            }
            setHoverHighlightedDomNodeRect(relativeRect);
            hoverHighlightedDomNodeRectRef.current = relativeRect;
        }
    }
    // Used to unlight a node when the mouse moves away
    // We only want to unhighlight the node if it is the currently highlighted node
    const unhighlightHoveredNode = (e, node) => {
        // First get the rect as if we were hovering over the highlighted node
        // Basically the same as setHighlightedNodeStates
        const nodeDomElement = document.getElementById(node?.id);
        const previewWindow = document.getElementById('previewWindow');
        // If we find it, we set the highlightedNodeDomElement state
        if (previewWindow && nodeDomElement) {
            const previewWindowRect = previewWindow.getBoundingClientRect();
            // Get the bounding rectangle of the node
            const rect = nodeDomElement.getBoundingClientRect();
            // Create a new rect that is relative to the preview window
            const relativeRect = {
                left: (rect.left - previewWindowRect.left) - 2,
                top: (rect.top - previewWindowRect.top) - 2,
                width: rect.width + 4,
                height: rect.height + 4
            };
            // Now check if this is the same as the currently highlighted node,
            // if so, clear it!
            if (hoverHighlightedDomNodeRectRef.current &&
                hoverHighlightedDomNodeRectRef.current.left === relativeRect.left &&
                hoverHighlightedDomNodeRectRef.current.top === relativeRect.top &&
                hoverHighlightedDomNodeRectRef.current.width === relativeRect.width &&
                hoverHighlightedDomNodeRectRef.current.height === relativeRect.height) {
                // If it is, we clear the hover state
                setHoverHighlightedDomNodeRect(null);
                hoverHighlightedDomNodeRectRef.current = null;
            }
        }
    }
    // Sometimes we need to force clearing the highlighted node state, for example when we start dragging a node
    const clearHighlightedHoverNodeState = () => {
        setHoverHighlightedDomNodeRect(null);
        hoverHighlightedDomNodeRectRef.current = null;
    }

    useEffect(() => {
        // console.log("EditablePreview: activeNode or visualisedNode changed, updating highlighted node states.", props.activeNode, props.visualisedNode, highlightedNodeStatesInvalidated);
        // Update our highlighted node state.
        // Resizeable nodes will have little handles in the corners.
        // Moveable nodes will have a border around them.
        // The top visualised node will be highlighted if there isn't a resizable or moveable subNode.
        let highlightedNode = null;
        if (props.activeNode !== props.visualisedNode && props.activeNode?.customisationVars) {
            const nodeMoveableOrResizable = canNodeBeMovedOrResized(props.activeNode, props.activeNode.customisationVars, campaignData, selectedFeatureNode, findCustomRendererKey(props.activeNode), props.activeNode.dataIndex ?? 0);
            // We only set a sub node as highlighted if it can be moved or resized.
            // if (nodeMoveableOrResizable.moveable || nodeMoveableOrResizable.resizable) {
                highlightedNode = props.activeNode;
                setHighlightedNodeDomElement(highlightedNode);
                setHighlightedNodeStates(highlightedNode, nodeMoveableOrResizable.moveable, nodeMoveableOrResizable.resizable, nodeMoveableOrResizable.widthScaleable, nodeMoveableOrResizable.heightScaleable);
            // }
        }
        if (props.visualisedNode && highlightedNode === null && props.visualisedNode?.customisationVars) {
            // If we don't have a highlighted node, we use the visualised node.
            // We highlight this whether it can be moved or resized or not for clarity.
            const nodeMoveableOrResizable = canNodeBeMovedOrResized(props.visualisedNode, props.visualisedNode.customisationVars, campaignData, selectedFeatureNode, findCustomRendererKey(props.visualisedNode), props.visualisedNode.dataIndex ?? 0);
            highlightedNode = props.visualisedNode;
            setHighlightedNodeDomElement(highlightedNode);
            setHighlightedNodeStates(highlightedNode, nodeMoveableOrResizable.moveable, nodeMoveableOrResizable.resizable, nodeMoveableOrResizable.widthScaleable, nodeMoveableOrResizable.heightScaleable);
        }
        if (highlightedNode === null) {
            // If we still don't have a highlighted node, clear the states.
            setHighlightedNodeDomElement(null);
            setHighlightedDomNodeRect(null);
            setHighlightedNodeIsMoveable(null);
            setHighlightedNodeIsResizable(null);
            setHighlightedNodeIsWidthScaleable(null);
            setHighlightedNodeIsHeightScaleable(null);
        }
        /* if (!draggingActive) {
            // Find any dom elements with the class 'v2-dragging-node-copy' and remove them
            const draggingNodeCopies = document.querySelectorAll('.v2-dragging-node-copy');
            draggingNodeCopies.forEach((copy) => {
                copy.remove();
            });
        } */
    }, [props.activeNode, props.visualisedNode, highlightedNodeStatesInvalidated]);

    const [draggingNode, setDraggingNode] = useState(null);
    const draggingNodeRef = useRef(draggingNode);
    const [draggingNodeDataIndex, setDraggingNodeDataIndex] = useState(0);
    const draggingNodeDataIndexRef = useRef(draggingNodeDataIndex);
    const [draggingStartPosition, setDraggingStartPosition] = useState(null);
    const draggingStartPositionRef = useRef(draggingStartPosition);
    const [draggingOffset, setDraggingOffset] = useState({ x: 0, y: 0 });
    const draggingOffsetRef = useRef(draggingOffset);
    const [draggingDomElementCopy, setDraggingDomElementCopy] = useState(null);
    const draggingDomElementCopyRef = useRef(draggingDomElementCopy);
    const draggingNodeTimeoutId = useRef(null);
    const draggingCancelled = useRef(false);
    const draggingActive = useRef(false);

    useEffect(() => {
        draggingNodeRef.current = draggingNode;
        draggingStartPositionRef.current = draggingStartPosition;
        draggingOffsetRef.current = draggingOffset;
        draggingDomElementCopyRef.current = draggingDomElementCopy;
        draggingNodeDataIndexRef.current = draggingNodeDataIndex;
    }, [draggingNode, draggingStartPosition, draggingOffset, draggingDomElementCopy, draggingNodeDataIndex]);
    const startDraggingNode = (e, node, dataIndex = 0) => {
        e.stopPropagation();
        e.preventDefault();
        /* if (draggingNodeTimeoutId.current) {
            clearTimeout(draggingNodeTimeoutId.current);
        } */
        draggingCancelled.current = false;
        /* draggingNodeTimeoutId.current = setTimeout(() => {
            if (draggingCancelled.current) {
                console.warn("Dragging was cancelled, not starting drag.");
                return;
            }
            console.log("Starting to drag node:", node);
            startDraggingNode_now(e, node);
        }, 100); // Delay to allow for click events to be processed first */
        // Dont start the actual dragging until we move the mouse a bit
        const initiateDrag = (moveEvent) => {
            if (draggingCancelled.current) {
                console.warn("Dragging was cancelled, not starting drag.");
                return;
            }
            // Check if the mouse has moved a bit
            const dx = moveEvent.clientX - e.clientX;
            const dy = moveEvent.clientY - e.clientY;
            if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
                // Start dragging now
                startDraggingNode_now(moveEvent, node, dataIndex);
                // Remove the mousemove listener
                window.removeEventListener('mousemove', initiateDrag);
                // Remove the mouseup listener
                window.removeEventListener('mouseup', cancelDrag);
            }
        }
        const cancelDrag = (e) => {
            window.removeEventListener('mousemove', initiateDrag);
            window.removeEventListener('mouseup', cancelDrag);
        }
        window.addEventListener('mousemove', initiateDrag);
        window.addEventListener('mouseup', cancelDrag);
    }
    const startDraggingNode_now = (e, node, dataIndex = 0) => {
        if (node !== highlightedNodeDomElement || !highlightedNodeIsMoveable) {
            // We need to step up the hierarchy to find the first moveable node
            console.log("Node is not highlighted or not moveable, stepping up the hierarchy to find a moveable node.", node);
            let currentNode = node;
            if (!canNodeBeMovedOrResized(currentNode, currentNode.customisationVars, campaignData, selectedFeatureNode, findCustomRendererKey(currentNode), currentNode.dataIndex ?? 0).moveable) {
                while (currentNode && currentNode.depth > 0) {
                    // console.log("Checking node:", currentNode, " moveable?");
                    const nodeMoveableOrResizable = canNodeBeMovedOrResized(currentNode, currentNode.customisationVars, campaignData, selectedFeatureNode, findCustomRendererKey(currentNode), currentNode.dataIndex ?? 0);
                    if (nodeMoveableOrResizable.moveable) {
                        // highlightedNode = props.activeNode;
                        // setHighlightedNodeDomElement(highlightedNode);
                        // setHighlightedNodeStates(highlightedNode, nodeMoveableOrResizable.moveable, nodeMoveableOrResizable.resizable, nodeMoveableOrResizable.widthScaleable, nodeMoveableOrResizable.heightScaleable);
                        node = currentNode; // Use this node instead
                        props.activateNodeFunction(node); // Activate the node that we are dragging
                        // invalidateHighlightedNodeStates(); // Invalidate the highlighted node states to force a re-render
                        break;
                    }
                    console.log("Stepping up to parent node:", currentNode.parent);
                    currentNode = currentNode.parent;
                }
                if (!currentNode || currentNode.depth === 0 || currentNode !== node) {
                    console.warn("Node is not moveable, cannot start dragging.");
                    return;
                }
            }
        }
        // e.stopPropagation();
        // e.preventDefault();
        const nodeDomElement = document.getElementById(node.id);
        if (!nodeDomElement) {
            console.warn("Node DOM element not found, cannot start dragging.");
            return;
        }
        console.log("--- STARTING DRAGGING NODE ---", node, "from element:", nodeDomElement, "at event:", e, nodeDomElement.getBoundingClientRect());
        // Calculate the offset from the mouse to the top-left corner of the node
        const rect = nodeDomElement.getBoundingClientRect();
        let offsetX = e.clientX - rect.left;
        let offsetY = e.clientY - rect.top;
        // If our node has a tranform applied, we need to adjust the offset
        const transform = window.getComputedStyle(nodeDomElement).transform;
        let matrix = null;
        if (transform && transform !== 'none') {
            matrix = new DOMMatrix(transform);
            offsetX += matrix.m41; // Adjust for translation
            offsetY += matrix.m42; // Adjust for translation
        }
        console.log("Dragging node offset:", e.clientX, e.clientY, rect.left, rect.top, offsetX, offsetY);
        // Get the preview window client rect
        const previewWindow = document.getElementById('previewWindow');
        if (!previewWindow) {
            console.warn("Preview window not found, cannot start dragging node.");
            return;
        }
        const previewWindowRect = previewWindow.getBoundingClientRect();
        setDraggingOffset({ x: offsetX, y: offsetY });
        setDraggingNode(node);
        setDraggingNodeDataIndex(dataIndex);
        // Make a copy of the dom element to drag
        const left = rect.left - previewWindowRect.left - (matrix?.m41 ?? 0);
        const top = rect.top - previewWindowRect.top - (matrix?.m42 ?? 0);
        const nodeCopy = nodeDomElement.cloneNode(true);
        nodeCopy.style.position = 'absolute';
        nodeCopy.style.left = `${left}px`;
        nodeCopy.style.top = `${top}px`;
        nodeCopy.style.zIndex = '1000'; // Bring it to the front
        nodeCopy.style.pointerEvents = 'none'; // Disable pointer events on the copy
        nodeCopy.classList.add('v2-dragging-node-copy');
        setDraggingDomElementCopy(nodeCopy);
        setDraggingStartPosition({ x: e.clientX, y: e.clientY });
        // Append the copy to the preview window
        // const previewWindow = document.getElementById('previewWindow');
        if (previewWindow) {
            previewWindow.appendChild(nodeCopy);
        } else {
            console.warn("Preview window not found, cannot append dragging node copy.");
        }
        // Set the cursor to grabbing
        document.body.style.cursor = 'grabbing';
        // Add event listeners for mouse move and mouse up
        window.addEventListener('mousemove', handleDraggingNode);
        window.addEventListener('mouseup', stopDraggingNode);
        // Switch off mouse events for the preview window
        if (previewWindow) {
            previewWindow.style.pointerEvents = 'none';
        }
        // Also add touch event listeners for mobile support
        draggingActive.current = true;

        clearHighlightedHoverNodeState(); // Clear the highlighted hover node state
    }
    const stopDraggingNode = (e) => {
        e.stopPropagation();
        e.preventDefault();

        /* if (!draggingActive.current) {
            console.warn("Dragging is not active, cannot stop dragging.");
            return;
        } */
        draggingActive.current = false;
        /* if (!draggingNodeRef.current || !draggingDomElementCopyRef.current) {
            console.warn("No dragging node or dragging dom element copy found, cannot stop dragging.");
            return;
        } */

        if (draggingNodeRef.current && draggingDomElementCopyRef.current) {
            updateVisualisedElementPositionData(draggingNodeRef.current, draggingDomElementCopyRef.current, null, null, null, null, draggingNodeDataIndexRef.current, e);
        }

        setDraggingOffset({ x: 0, y: 0 });
        setDraggingNode(null);
        // Remove the event listeners for mouse move and mouse up
        const previewWindow = document.getElementById('previewWindow');
        if (previewWindow) {
            previewWindow.style.pointerEvents = 'auto';
        }
        window.removeEventListener('mousemove', handleDraggingNode);
        window.removeEventListener('mouseup', stopDraggingNode);
        
        // Reset the cursor to default
        document.body.style.cursor = null;
        // Remove the dragging copy from the preview window
        const draggingDomElementCopy_loc = draggingDomElementCopyRef.current;
        if (draggingDomElementCopy_loc) {
            // Is the draggingDomElementCopy_loc still in the preview window?
            draggingDomElementCopy_loc.remove();
            setDraggingDomElementCopy(null);
        } else {
            console.warn("Preview window or dragging node copy not found, cannot remove dragging node copy.");
        }
        
        if (draggingNodeRef.current) {
            props.activateNodeFunction(draggingNodeRef.current); // Activate the node that was dragged
        }
        invalidateHighlightedNodeStates(); // Invalidate the highlighted node states to force a re-render
    }
    const handleDraggingNode = (e) => {
        const draggingNode_loc = draggingNodeRef.current;
        if (!draggingNode_loc) return;
        e.stopPropagation();
        e.preventDefault();
        // Calculate the new position of the node based on the mouse position and the offset
        const previewWindow = document.getElementById('previewWindow');
        if (!previewWindow) {
            console.warn("Preview window not found, cannot handle dragging node.");
            return;
        }

        const draggingOffset_loc = draggingOffsetRef.current;
        const draggingDomElementCopy_loc = draggingDomElementCopy || draggingDomElementCopyRef.current;
        // console.log("Dragging node:", draggingNode_loc, "at offset:", draggingOffset_loc, "with copy:", draggingDomElementCopy_loc);

        const previewWindowRect = previewWindow.getBoundingClientRect();
        const newLeft = e.clientX - previewWindowRect.left - draggingOffset_loc.x;
        const newTop = e.clientY - previewWindowRect.top - draggingOffset_loc.y;
        // console.log("New position:", newLeft, newTop, "from mouse:", e.clientX, e.clientY, "preview rect:", previewWindowRect);
        // Update the position of the dragging node copy
        if (draggingDomElementCopy_loc) {
            draggingDomElementCopy_loc.style.left = `${newLeft}px`;
            draggingDomElementCopy_loc.style.top = `${newTop}px`;
            draggingDomElementCopy_loc.style.bottom = null;
            draggingDomElementCopy_loc.style.right = null;
        } else {
            console.warn("Dragging node copy not found, cannot update position.");
        }

    }

    const [resizeHandleBeingDragged, setResizeHandleBeingDragged] = useState(null);
    const resizeHandleBeingDraggedRef = useRef(resizeHandleBeingDragged);
    const [startSizeRect, setStartSizeRect] = useState(null);
    const startSizeRectRef = useRef(startSizeRect);
    const mirrorResizeXRef = useRef(false);
    const mirrorResizeYRef = useRef(false);
    useEffect(() => {
        resizeHandleBeingDraggedRef.current = resizeHandleBeingDragged;
        startSizeRectRef.current = startSizeRect;
    }, [resizeHandleBeingDragged, startSizeRect]);
    const startDraggingResizeHandle = (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        setResizeHandleBeingDragged(e.target.id);
        // Find the rect of our hilight box (id='boxHighlight')
        // We can use this to restrict where we can drag the resize handle (ie. ones on the right can't go past the left edge of the box, etc.)
        const boxHighlight = document.getElementById('boxHighlight');
        if (!boxHighlight) {
            console.warn("Box highlight not found, cannot start dragging resize handle.");
            return;
        }
        const boxHighlightRect = boxHighlight.getBoundingClientRect();
        // Find the rect of the preview window
        const previewWindow = document.getElementById('previewWindow');
        if (!previewWindow) {
            console.warn("Preview window not found, cannot start dragging resize handle.");
            return;
        }
        const previewWindowRect = previewWindow.getBoundingClientRect();
        // Set the start size rect to the box highlight rect, but relative to the preview window
        setStartSizeRect({
            left: boxHighlightRect.left - previewWindowRect.left,
            top: boxHighlightRect.top - previewWindowRect.top,
            right: boxHighlightRect.right - previewWindowRect.left,
            bottom: boxHighlightRect.bottom - previewWindowRect.top,
            width: boxHighlightRect.width,
            height: boxHighlightRect.height,
            centerX: boxHighlightRect.left - previewWindowRect.left + (boxHighlightRect.width / 2),
            centerY: boxHighlightRect.top - previewWindowRect.top + (boxHighlightRect.height / 2)
        });
        
        console.log("Starting to drag resize handle:", e.target.id);
        // Set the cursor to grabbing
        // document.body.style.cursor = 'grabbing';
        // Add event listeners for mouse move and mouse up
        window.addEventListener('mousemove', handleDraggingResizeHandle);
        window.addEventListener('mouseup', stopDraggingResizeHandle);
        // Remove pointer events from the preview window
        if (previewWindow) {
            previewWindow.style.pointerEvents = 'none';
        }
        // Also add touch event listeners for mobile support
    }
    const stopDraggingResizeHandle = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const resizeHandleBeingDragged_loc = resizeHandleBeingDraggedRef.current;
        if (!resizeHandleBeingDragged_loc) {
            console.warn("No resize handle being dragged, cannot stop dragging.");
            return;
        }
        console.log("Stopping dragging resize handle:", resizeHandleBeingDragged_loc);
        // Remove the event listeners for mouse move and mouse up
        window.removeEventListener('mousemove', handleDraggingResizeHandle);
        window.removeEventListener('mouseup', stopDraggingResizeHandle);
        // Reset the cursor to default
        document.body.style.cursor = null;

        // Update the node's size based on the boxHighlight position and size
        // We need to apply a 2 pixel inner buffer to the box highlight to find the resulting position and size
        const boxHighlight = document.getElementById('boxHighlight');
        if (!boxHighlight) {
            console.warn("Box highlight not found, cannot stop dragging resize handle.");
            return;
        }
        const boxHighlightRect = boxHighlight.getBoundingClientRect();
        // Find the preview window rect
        const previewWindow = document.getElementById('previewWindow');
        if (!previewWindow) {
            console.warn("Preview window not found, cannot stop dragging resize handle.");
            return;
        } else {
            previewWindow.style.pointerEvents = 'auto';
        }
        const previewWindowRect = previewWindow.getBoundingClientRect();
        // Get the new position and size of the box highlight
        const newLeft = boxHighlightRect.left - previewWindowRect.left + 2; // Add 2px inner buffer
        const newTop = boxHighlightRect.top - previewWindowRect.top + 2; // Add 2px inner buffer
        const newWidth = boxHighlightRect.width - 4; // Subtract 4px for the 2px inner buffer on each side
        const newHeight = boxHighlightRect.height - 4; // Subtract 4px for the 2px inner buffer on each side
        // Update the node's position and dimensions based on the new position and size
        // First find the dom element for the highlighted node
        const highlightedNode = highlightedNodeDomElementRef.current;
        if (!highlightedNode) {
            console.warn("Highlighted node not found, cannot stop dragging resize handle.");
            return;
        }
        // Find the actual dom element for the highlighted node
        const nodeDomElement = document.getElementById(highlightedNode.id);
        if (!nodeDomElement) {
            console.warn("Node DOM element not found, cannot stop dragging resize handle.");
            return;
        }

        // We may need to adjust the offset depending on which side we are dragging from and if we are mirroring
        if (!mirrorResizeXRef.current || !mirrorResizeYRef.current) {
            let positionModule = null;
            if (highlightedNode.config && highlightedNode.config.children) {
                for (const child of highlightedNode.config.children) {
                    if (child.type === 'module' && child.module === 'position') {
                        positionModule = child;
                        break;
                    }
                }
            }
            
            let leftAligned = false;
            let topAligned = false;
            let rightAligned = false;
            let bottomAligned = false;

            if (positionModule) {
                // Find position field in the position module, we only care about this if it has a dataLink
                let positionField = null;
                for (const field of positionModule.fields) {
                    if (field.dataLink && field.field?.includes('position-position')) {
                        positionField = field;
                        break;
                    }
                }
                // console.log("---Resizing: Position field found:", positionField);
                const positionSetting = getCustomisationValue(highlightedNode.customisationVars, positionField?.dataLink, highlightedNode.dataIndex ?? 0, null, highlightedNode);
                // Find the key in positionField who's value matches positionSetting
                if (positionSetting) {
                    const matchingPositionKey = Object.keys(positionField).find(key => positionField[key] === positionSetting);
                    if (matchingPositionKey.includes('left')) {
                        leftAligned = true;
                    }
                    if (matchingPositionKey.includes('top')) {
                        topAligned = true;
                    }
                    if (matchingPositionKey.includes('right')) {
                        rightAligned = true;
                    }
                    if (matchingPositionKey.includes('bottom')) {
                        bottomAligned = true;
                    }
                }
            }
            if (!mirrorResizeXRef.current) {
                if (leftAligned && boxHighlightRect.left - previewWindowRect.left !== Math.floor(startSizeRectRef.current.left)) {
                    // console.log("Resize handle: left changed from", startSizeRectRef.current.left, "to", boxHighlightRect.left - previewWindowRect.left);
                    const leftDifference = ((boxHighlightRect.left - previewWindowRect.left) - Math.floor(startSizeRectRef.current.left));
                    // console.log("Resize handle: Left offset difference:", leftDifference, leftDifference * previewScaleRef.current, leftDifference / previewScaleRef.current);
                    // console.log("Resize handle: Offset adjustment needed:", leftDifference / previewScaleRef.current);
                    adjustOffsetXBy(highlightedNode, 0, Math.round(leftDifference / previewScaleRef.current));
                }
                if (rightAligned && boxHighlightRect.right - previewWindowRect.left !== Math.floor(startSizeRectRef.current.right)) {
                    // console.log("Resize handle: right changed from", startSizeRectRef.current.right, "to", boxHighlightRect.right - previewWindowRect.left);
                    const rightDifference = ((boxHighlightRect.right - previewWindowRect.left) - Math.floor(startSizeRectRef.current.right));
                    // console.log("Resize handle: Right offset difference:", rightDifference, rightDifference * previewScaleRef.current, rightDifference / previewScaleRef.current);
                    // console.log("Resize handle: Offset adjustment needed:", rightDifference / previewScaleRef.current);
                    adjustOffsetXBy(highlightedNode, 0, Math.round(rightDifference / previewScaleRef.current));
                }
            }
            if (!mirrorResizeYRef.current) {
                if (topAligned && boxHighlightRect.top - previewWindowRect.top !== Math.floor(startSizeRectRef.current.top)) {
                    // console.log("Resize handle: top changed from", startSizeRectRef.current.top, "to", boxHighlightRect.top - previewWindowRect.top);
                    const topDifference = ((boxHighlightRect.top - previewWindowRect.top) - Math.floor(startSizeRectRef.current.top));
                    // console.log("Resize handle: Top offset difference:", topDifference, topDifference * previewScaleRef.current, topDifference / previewScaleRef.current);
                    // console.log("Resize handle: Offset adjustment needed:", topDifference / previewScaleRef.current);
                    adjustOffsetYBy(highlightedNode, 0, Math.round(topDifference / previewScaleRef.current));
                }
                if (bottomAligned && boxHighlightRect.bottom - previewWindowRect.top !== Math.floor(startSizeRectRef.current.bottom)) {
                    // console.log("Resize handle: bottom changed from", startSizeRectRef.current.bottom, "to", boxHighlightRect.bottom - previewWindowRect.top);
                    const bottomDifference = ((boxHighlightRect.bottom - previewWindowRect.top) - Math.floor(startSizeRectRef.current.bottom));
                    // console.log("Resize handle: Bottom offset difference:", bottomDifference, bottomDifference * previewScaleRef.current, bottomDifference / previewScaleRef.current);
                    // console.log("Resize handle: Offset adjustment needed:", bottomDifference / previewScaleRef.current);
                    adjustOffsetYBy(highlightedNode, 0, Math.round(bottomDifference / previewScaleRef.current));
                }
            }
        }

        // Update the position of the node based on the new position
        // if (!mirrorResizeXRef.current) nodeDomElement.style.left = `${newLeft}px`;
        // if (!mirrorResizeYRef.current) nodeDomElement.style.top = `${newTop}px`;
        // We only need to update the position if the node is moveable
        // This is no longer needed with resize mirroring!!!
        // if (highlightedNodeIsMoveableRef.current) {
            // updateVisualisedElementPositionData(highlightedNode, nodeDomElement, !mirrorResizeXRef.current ? newLeft + previewWindowRect.left : null, !mirrorResizeYRef.current ? newTop + previewWindowRect.top : null, newWidth, newHeight);
        // }

        // Update the visualised element dimension data
        updateVisualisedElementDimensionData(highlightedNode, Math.round(newWidth / (previewScaleRef.current ?? previewScale)), Math.round(newHeight / (previewScaleRef.current ?? previewScale)));
        

        invalidateHighlightedNodeStates(); // Invalidate the highlighted node states to force a re-render
    }
    const handleDraggingResizeHandle = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const resizeHandleBeingDragged_loc = resizeHandleBeingDraggedRef.current;
        if (!resizeHandleBeingDragged_loc) {
            console.warn("No resize handle being dragged, cannot handle dragging.");
            return;
        }
        console.log("Handling dragging resize handle:", resizeHandleBeingDragged_loc);
        // move the handle to the mouse position
        const previewWindow = document.getElementById('previewWindow');
        if (!previewWindow) {
            console.warn("Preview window not found, cannot handle dragging resize handle.");
            return;
        }
        const previewWindowRect = previewWindow.getBoundingClientRect();
        let newLeft = e.clientX - previewWindowRect.left;
        let newTop = e.clientY - previewWindowRect.top;

        // We base mirroring on the position module, centered on the x axis = mirror on the x axis and centered on the y axis = mirror on the y axis
        const highlightedNode = highlightedNodeDomElementRef.current;
        // We might need to mirror the resizing, depending on the handle being dragged
        let mirrorX = highlightedNode.forceMirrorXResize ? true : false;
        let mirrorY = highlightedNode.forceMirrorYResize ? true : false;
        const forceProportionalScaling = highlightedNode.forceProportionalScaling ?? false;
        let positionModule = null;
        if (highlightedNode.config && highlightedNode.config.children) {
            for (const child of highlightedNode.config.children) {
                if (child.type === 'module' && child.module === 'position') {
                    positionModule = child;
                    break;
                }
            }
        }
        if (positionModule) {
            // Find position field in the position module, we only care about this if it has a dataLink
            let positionField = null;
            for (const field of positionModule.fields) {
                if (field.dataLink && field.field?.includes('position-position')) {
                    positionField = field;
                    break;
                }
            }
            // console.log("---Resizing: Position field found:", positionField);
            const positionSetting = getCustomisationValue(highlightedNode.customisationVars, positionField?.dataLink, highlightedNode.dataIndex ?? 0, null, highlightedNode);
            // Find the key in positionField who's value matches positionSetting
            if (positionSetting) {
                const matchingPositionKey = Object.keys(positionField).find(key => positionField[key] === positionSetting);
                if (matchingPositionKey === 'top-center-value' || matchingPositionKey === 'center-value' || matchingPositionKey === 'bottom-center-value') {
                    mirrorX = true;
                }
                if (matchingPositionKey === 'center-left-value' || matchingPositionKey === 'center-value' || matchingPositionKey === 'center-right-value') {
                    mirrorY = true;
                }
            }
            if (!mirrorX && !mirrorY) {
                // We might need to base mirror x / y on vertical and horizontal alignment instead
                for (const field of positionModule.fields) {
                    if (field["alignment-y"]?.dataLink && field.field?.includes('module-position-alignment')) {
                        const verticalAlignment = getCustomisationValue(highlightedNode.customisationVars, field["alignment-y"]?.dataLink, highlightedNode.dataIndex ?? 0, null, highlightedNode);
                        const middleValue = field['alignment-middle-value'];
                        if (verticalAlignment && middleValue && verticalAlignment === middleValue) {
                            mirrorY = true;
                        }
                    }
                    if (field["alignment-x"]?.dataLink && field.field?.includes('module-position-alignment')) {
                        const horizontalAlignment = getCustomisationValue(highlightedNode.customisationVars, field["alignment-x"]?.dataLink, highlightedNode.dataIndex ?? 0, null, highlightedNode);
                        const centerValue = field['alignment-center-value'];
                        if (horizontalAlignment && centerValue && horizontalAlignment === centerValue) {
                            mirrorX = true;
                        }
                    }
                }
            }
            mirrorResizeXRef.current = mirrorX;
            mirrorResizeYRef.current = mirrorY;
        }

        // Get the start size rect
        const startSizeRect_loc = startSizeRectRef.current;
        if (!startSizeRect_loc) {
            console.warn("Start size rect not found, cannot handle dragging resize handle.");
            return;
        }
        // Restrict the position of the resize handle based on the start size rect
        if (resizeHandleBeingDragged_loc.includes('w')) {
            // Left resize handle
            newLeft = Math.min(newLeft, startSizeRect_loc.left + startSizeRect_loc.width - 10);
            if (resizeHandleBeingDragged_loc === 'w') {
                // Fix y position to half way down the box highlight
                newTop = startSizeRect_loc.top + startSizeRect_loc.height / 2;
            }
        } else if (resizeHandleBeingDragged_loc.includes('e')) {
            // Right resize handle
            newLeft = Math.max(newLeft, startSizeRect_loc.left + 10);
            if (resizeHandleBeingDragged_loc === 'e') {
                // Fix y position to half way down the box highlight
                newTop = startSizeRect_loc.top + startSizeRect_loc.height / 2;
            }
        }
        if (resizeHandleBeingDragged_loc.includes('n')) {
            // Top resize handle
            newTop = Math.min(newTop, startSizeRect_loc.top + startSizeRect_loc.height - 10);
            if (resizeHandleBeingDragged_loc === 'n') {
                // Fix x position to half way across the box highlight
                newLeft = startSizeRect_loc.left + startSizeRect_loc.width / 2;
            }
        } else if (resizeHandleBeingDragged_loc.includes('s')) {
            // Bottom resize handle
            newTop = Math.max(newTop, startSizeRect_loc.top + 10);
            if (resizeHandleBeingDragged_loc === 's') {
                // Fix x position to half way across the box highlight
                newLeft = startSizeRect_loc.left + startSizeRect_loc.width / 2;
            }
        }

        // Resize the box highlight based on the position of the resize handle
        const boxHighlight = document.getElementById('boxHighlight');
        if (!boxHighlight) {
            console.warn("Box highlight not found, cannot update size.");
            return;
        }
        
        // Move the other handles based on the position of the resize handle
        const resizeHandleIds = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
        let boxLeft = boxHighlight.offsetLeft;
        let boxTop = boxHighlight.offsetTop;
        let boxRight = boxLeft + boxHighlight.offsetWidth;
        let boxBottom = boxTop + boxHighlight.offsetHeight;
        
        for (let i = 0; i < resizeHandleIds.length; i++) {
            const handleId = resizeHandleIds[i];

            const handleDomElement = document.getElementById(handleId);
            if (!handleDomElement) {
                console.warn(`Resize handle ${handleId} DOM element not found, cannot update position.`);
                continue;
            }
            // If the handle is on the left or right, we need to adjust the left position
            if (handleId.includes('w') && resizeHandleBeingDragged_loc.includes('w')) {
                // Left handle
                const xDistanceMoved = newLeft - boxLeft;
                boxLeft = newLeft;
                if (mirrorX && xDistanceMoved != 0) {
                    // Match the movement on the e, ne, se handles and boxRight
                    boxRight -= xDistanceMoved;
                }
            }
            if (handleId.includes('e') && resizeHandleBeingDragged_loc.includes('e')) {
                // Right handle
                const xDistanceMoved = newLeft - boxRight;
                boxRight = newLeft;
                if (mirrorX && xDistanceMoved != 0) {
                    // Match the movement on the w, nw, sw handles and boxLeft
                    boxLeft -= xDistanceMoved;
                }
            }
            // If the handle is on the top or bottom, we need to adjust the top position
            if (handleId.includes('n') && resizeHandleBeingDragged_loc.includes('n')) {
                // Top handle
                const yDistanceMoved = newTop - boxTop;
                boxTop = newTop;
                if (mirrorY && yDistanceMoved != 0) {
                    // Match the movement on the s, se, sw handles and boxBottom
                    boxBottom -= yDistanceMoved;
                }
            }
            if (handleId.includes('s') && resizeHandleBeingDragged_loc.includes('s')) {
                // Bottom handle
                const yDistanceMoved = newTop - boxBottom;
                boxBottom = newTop;
                if (mirrorY && yDistanceMoved != 0) {
                    // Match the movement on the s, se, sw handles and boxBottom
                    boxTop -= yDistanceMoved;
                }
            }
        }

        if (forceProportionalScaling) {
            // Get the ratio of the new width and height based on the start size rect
            const startWidth = startSizeRect_loc.width;
            const startHeight = startSizeRect_loc.height;
            const widthRatio = (boxRight - boxLeft) / startWidth;
            const heightRatio = (boxBottom - boxTop) / startHeight;
            // Find the smaller ratio
            let largerRatio = Math.max(widthRatio, heightRatio);
            // Ensure the smaller ratio is at least 10 pixels in size
            const minSize = 10;
            if (startWidth * largerRatio < minSize) {
                largerRatio = minSize / startWidth;
            }
            if (startHeight * largerRatio < minSize) {
                largerRatio = minSize / startHeight;
            }
            const proportionalWidth = startWidth * largerRatio;
            const proportionalHeight = startHeight * largerRatio;
            if (mirrorX) {
                // Adjust boxLeft and boxRight to be centered on the original center
                const centerX = startSizeRectRef.current.centerX; // (boxLeft + boxRight) / 2;
                boxLeft = centerX - proportionalWidth / 2;
                boxRight = centerX + proportionalWidth / 2;
            } else {
                // adjust left or right based on which handle is being dragged
                if (resizeHandleBeingDragged_loc.includes('w')) {
                    boxLeft = boxRight - proportionalWidth;
                } else {
                    boxRight = boxLeft + proportionalWidth;
                }
            }
            if (mirrorY) {
                // Adjust boxTop and boxBottom to be centered on the original center
                const centerY = startSizeRectRef.current.centerY; // (boxTop + boxBottom) / 2;
                boxTop = centerY - proportionalHeight / 2;
                boxBottom = centerY + proportionalHeight / 2;
            } else {
                // adjust top or bottom based on which handle is being dragged
                if (resizeHandleBeingDragged_loc.includes('n')) {
                    boxTop = boxBottom - proportionalHeight;
                } else {
                    boxBottom = boxTop + proportionalHeight;
                }
            }
        }

        if (boxLeft <= boxRight - 10) {
            // Match all resize handles to the edges of our box highlight
            for (let i = 0; i < resizeHandleIds.length; i++) {
                const handleId = resizeHandleIds[i];
                const handleDomElement = document.getElementById(handleId);
                if (!handleDomElement) continue;
                if (handleId.includes('w')) {
                    handleDomElement.style.left = `${boxLeft}px`;
                }
                if (handleId.includes('e')) {
                    handleDomElement.style.left = `${boxRight}px`;
                }
                if (handleId === 'n' || handleId === 's') {
                    // Set the left position to half way across the box highlight
                    handleDomElement.style.left = `${boxLeft + (boxRight - boxLeft) / 2}px`;
                }
            }

            // Update the size of the box highlight based on the position of the resize handle
            boxHighlight.style.left = `${boxLeft}px`;
            boxHighlight.style.width = `${boxRight - boxLeft}px`;
        
            // Get the dom element of the resize handle
            const resizeHandleDomElement = document.getElementById(resizeHandleBeingDragged_loc);
            if (!resizeHandleDomElement) {
                console.warn("Resize handle DOM element not found, cannot update position.");
                return;
            }
            // Update the position of the resize handle
            if (!forceProportionalScaling) {
                resizeHandleDomElement.style.left = `${newLeft}px`;
            }
        }

        if (boxTop <= boxBottom - 10) {
            // Match all resize handles to the edges of our box highlight
            for (let i = 0; i < resizeHandleIds.length; i++) {
                const handleId = resizeHandleIds[i];
                const handleDomElement = document.getElementById(handleId);
                if (!handleDomElement) continue;
                if (handleId.includes('n')) {
                    handleDomElement.style.top = `${boxTop}px`;
                }
                if (handleId.includes('s')) {
                    handleDomElement.style.top = `${boxBottom}px`;
                }
                if (handleId === 'e' || handleId === 'w') {
                    // Set the top position to half way down the box highlight
                    handleDomElement.style.top = `${boxTop + (boxBottom - boxTop) / 2}px`;
                }
            }

            // Update the size of the box highlight based on the position of the resize handle
            boxHighlight.style.top = `${boxTop}px`;
            boxHighlight.style.height = `${boxBottom - boxTop}px`;
        
            // Get the dom element of the resize handle
            const resizeHandleDomElement = document.getElementById(resizeHandleBeingDragged_loc);
            if (!resizeHandleDomElement) {
                console.warn("Resize handle DOM element not found, cannot update position.");
                return;
            }
            // Update the position of the resize handle
            if (!forceProportionalScaling) {
                resizeHandleDomElement.style.top = `${newTop}px`;
            }
        }
    }

    const [editingTextContentData, setEditingTextContentData] = useState(null);
    useEffect(() => {
        if (editingTextContentData === null) {
            props.forceUpdateModuleListFunc?.();
        }
    }, [editingTextContentData]);
    const contentClicked = (e, node, index, contentData, textEditMode = false, selectOnly = false) => {
        e.stopPropagation();
        e.preventDefault();
        if (draggingActive.current) {
            console.warn("Dragging is active, cancelling content click.");
            return;
        }
        console.log("--- Content clicked:", e, node, index, contentData);
        draggingCancelled.current = true; // Cancel any ongoing dragging
        props.activateNodeFunction(node);

        if (!selectOnly && contentData && (contentData?.contentConfig?.module === 'copy' || textEditMode) && contentData?.contentConfig?.dataLink) {
            setEditingTextContentData(contentData.contentConfig);
            console.log("Setting editing text content data:", contentData.contentConfig);
        }
    }

    const updateVisualisedElementPositionData = (node, droppedDomNode, forceDomLeft = null, forceDomTop = null, forceDomWidth = null, forceDomHeight = null, dataIndex = 0, e) => {
        // Intelligently reposition the node based on the dropped position
        // If the node has a position module, we update the position data
        // We are going to align it to the side it is closest to and then apply any offsets so we end up exactly where the user dropped it
        // If it doesn't have position dataLinks, we will just apply the offsets directly to the node
        // Unless we don't have offset dataLinks, in which case we will do nothing
        // We shouldn't end up doing nothing as dragging is enabled based on the exisitence of these dataLinks
        console.log("Updating visualised element position data for node:", node, "with droppedDomNode:", droppedDomNode);
        // Find position module in the node.config.children
        let positionModule = null;
        const dragAdjustsPosition = (node?.config?.groupSettings?.adjustPosWhileDragging ?? true);
        if (node.config && node.config.children) {
            for (const child of node.config.children) {

                if (child.type === 'module') {
                    let hideModule = false;
                    if (child?.moduleSettings?.visualisationToggle !== undefined && child?.moduleSettings?.visualisationToggle !== null) {
                        const toggleValue = getCustomisationValue(node.customisationVars, child.moduleSettings.visualisationToggle, dataIndex ?? 0, null/* customVisualisedNodeKey */, node);
                        if ((child?.moduleSettings?.reverseVisualisationToggle === true && toggleValue) || (child?.moduleSettings?.reverseVisualisationToggle !== true && !toggleValue)) {
                            hideModule = true;
                        }
                    }
                    if (!hideModule) {
                        if (child?.moduleSettings?.advancedVisualisationToggleLink && child?.moduleSettings?.advancedVisualisationToggleValue !== undefined && child?.moduleSettings?.advancedVisualisationToggleValue !== null) {
                            const advancedToggleValue = getCustomisationValue(node.customisationVars, child.moduleSettings.advancedVisualisationToggleLink, dataIndex ?? 0, null/* customVisualisedNodeKey */, node);
                            if (((advancedToggleValue === child.moduleSettings.advancedVisualisationToggleValue || Number(advancedToggleValue) === Number(child.moduleSettings.advancedVisualisationToggleValue) || advancedToggleValue?.toString() === child.moduleSettings.advancedVisualisationToggleValue) && child?.moduleSettings?.advancedVisualisationToggleReverse !== true) || ((advancedToggleValue !== child.moduleSettings.advancedVisualisationToggleValue && Number(advancedToggleValue) !== Number(child.moduleSettings.advancedVisualisationToggleValue) && advancedToggleValue?.toString() !== child.moduleSettings.advancedVisualisationToggleValue) && child?.moduleSettings?.advancedVisualisationToggleReverse === true)) {
                                hideModule = true;
                            }
                        }
                    }

                    if (!hideModule && child?.moduleSettings?.visualise !== false && child.module === 'position') {
                        positionModule = child;
                        break;
                    }
                }
                
            }
        }
        if (positionModule) {
            // Find position field in the position module, we only care about this if it has a dataLink
            let positionField = null;
            for (const field of positionModule.fields) {
                if (field.dataLink && field.field?.includes('position-position')) {
                    positionField = field;
                    break;
                }
            }
            // Find the offset-x and offset-y fields in the position module, we only care about these if they have a dataLink
            let offsetXField = null;
            let offsetYField = null;
            for (const field of positionModule.fields) {
                if (field.field?.includes('position-offset')) {
                    if (field['offset-x']?.dataLink) {
                        offsetXField = field['offset-x'];
                    }
                    if (field['offset-y']?.dataLink) {
                        offsetYField = field['offset-y'];
                    }
                }
            }
            // If we have a position field, snap the node to the closest position
            // The position we need is a string like 'top-left-value', 'top-right-value', 'center-value', etc.
            // We can then look up the value we need and set the position dataLink to that value
            
            // Get the bounding rectangle of the dropped DOM node
            const domRect = droppedDomNode.getBoundingClientRect();
            // Copy the main left, right, top, bottom, width, height values so we can change them if we need to
            const rect = {
                left: domRect.left,
                right: domRect.right,
                top: domRect.top,
                bottom: domRect.bottom,
                width: domRect.width,
                height: domRect.height
            };
            if (forceDomLeft !== null) {
                console.log("Forcing left position to:", forceDomLeft);
                rect.left = forceDomLeft;
            }
            if (forceDomTop !== null) {
                console.log("Forcing top position to:", forceDomTop);
                rect.top = forceDomTop;
            }
            if (forceDomWidth !== null) {
                console.log("Forcing width to:", forceDomWidth);
                rect.width = forceDomWidth;
                rect.right = rect.left + rect.width;
            }
            if (forceDomHeight !== null) {
                console.log("Forcing height to:", forceDomHeight);
                rect.height = forceDomHeight;
                rect.bottom = rect.top + rect.height;
            }
            const previewWindow = document.getElementById('previewWindow');
            if (!previewWindow) {
                console.warn("Preview window not found, cannot update position data.");
                return;
            }
            const previewWindowRect = previewWindow.getBoundingClientRect();

            let positionDataLinkToUse = positionField?.dataLink;
            let positionDataIndexToUse = dataIndex;
            let offsetXDataLinkToUse = offsetXField?.dataLink;
            let offsetXDataIndexToUse = dataIndex;
            let offsetYDataLinkToUse = offsetYField?.dataLink;
            let offsetYDataIndexToUse = dataIndex;
            // Have these been overridden on the node?
            if (node.overrideVisualisedDataLinks) {
                if (node.overrideVisualisedDataLinks.position) {
                    positionDataLinkToUse = node.overrideVisualisedDataLinks.position || positionDataLinkToUse;
                    positionDataIndexToUse = node.overrideVisualisedDataLinks.positionDataIndex !== undefined && node.overrideVisualisedDataLinks.positionDataIndex !== null ? node.overrideVisualisedDataLinks.positionDataIndex : positionDataIndexToUse;
                }
                if (node.overrideVisualisedDataLinks.offsetX) {
                    offsetXDataLinkToUse = node.overrideVisualisedDataLinks.offsetX || offsetXDataLinkToUse;
                    offsetXDataIndexToUse = node.overrideVisualisedDataLinks.offsetXDataIndex !== undefined && node.overrideVisualisedDataLinks.offsetXDataIndex !== null ? node.overrideVisualisedDataLinks.offsetXDataIndex : offsetXDataIndexToUse;
                }
                if (node.overrideVisualisedDataLinks.offsetY) {
                    offsetYDataLinkToUse = node.overrideVisualisedDataLinks.offsetY || offsetYDataLinkToUse;
                    offsetYDataIndexToUse = node.overrideVisualisedDataLinks.offsetYDataIndex !== undefined && node.overrideVisualisedDataLinks.offsetYDataIndex !== null ? node.overrideVisualisedDataLinks.offsetYDataIndex : offsetYDataIndexToUse;
                }
            }

            let newPositionValue = getCustomisationValue(node.customisationVars, positionDataLinkToUse, positionDataIndexToUse, null, node) || null;

            // Find the center of the preview window
            const previewWindowCenterX = (previewWindowRect.left + previewWindowRect.right) / 2;
            const previewWindowCenterY = (previewWindowRect.top + previewWindowRect.bottom) / 2;
            // A segment is 1/3 of the preview window width or height
            const segmentWidth = previewWindowRect.width / 3;
            const segmentHeight = previewWindowRect.height / 3;
            // Find the center of the rect
            const rectCenterX = (rect.left + rect.right) / 2;
            const rectCenterY = (rect.top + rect.bottom) / 2;

            if (positionField && positionField.dataLink) {
                // We treat the preview window as 9 sections, top-left, top-center, top-right, center-left, center, center-right, bottom-left, bottom-center, bottom-right
                // We will snap the position to the closest section based on the center of the rect
                // If the rect is in the top-left corner, we will snap to top-left
                // If the rect is in the top-right corner, we will snap to top-right
                // etc.
                
                if (dragAdjustsPosition) {
                    // Determine the new position value based on the center of the rect
                    if (rectCenterY - previewWindowRect.top < segmentHeight) {
                        // Top row
                        if (rectCenterX - previewWindowRect.left < segmentWidth) {
                            // Top-left
                            newPositionValue = 'top-left-value';
                        } else if (rectCenterX - previewWindowRect.left < segmentWidth * 2) {
                            // Top-center
                            newPositionValue = 'top-center-value';
                        } else {
                            // Top-right
                            newPositionValue = 'top-right-value';
                        }
                    } else if (rectCenterY - previewWindowRect.top < segmentHeight * 2) {
                        // Middle row
                        if (rectCenterX - previewWindowRect.left < segmentWidth) {
                            // Center-left
                            newPositionValue = 'center-left-value';
                        } else if (rectCenterX - previewWindowRect.left < segmentWidth * 2) {
                            // Center
                            newPositionValue = 'center-value';
                        } else {
                            // Center-right
                            newPositionValue = 'center-right-value';
                        }
                    } else {
                        // Bottom row
                        if (rectCenterX - previewWindowRect.left < segmentWidth) {
                            // Bottom-left
                            newPositionValue = 'bottom-left-value';
                        } else if (rectCenterX - previewWindowRect.left < segmentWidth * 2) {
                            // Bottom-center
                            newPositionValue = 'bottom-center-value';
                        } else {
                            // Bottom-right
                            newPositionValue = 'bottom-right-value';
                        }
                    }
                    console.log("New position value:", newPositionValue);
                    // Bear in mind that the value we have just determined may not exist! So we need to check that and snap to the closest one
                    if (!positionField[newPositionValue]) {
                        console.warn(`Position field does not have a value for ${newPositionValue}, cannot update position data.`);
                        // We can try to find the closest position value
                        // There is a good chance we will have at least a top / center / bottom value
                        if (newPositionValue.startsWith('top')) {
                            if (positionField['top-left-value']) {
                                newPositionValue = 'top-left-value';
                            } else if (positionField['top-center-value']) {
                                newPositionValue = 'top-center-value';
                            } else if (positionField['top-right-value']) {
                                newPositionValue = 'top-right-value';
                            } else {
                                console.warn("No valid top position values found, cannot update position data.");
                                return;
                            }
                        } else
                        if (newPositionValue.startsWith('bottom')) {
                            if (positionField['bottom-left-value']) {
                                newPositionValue = 'bottom-left-value';
                            } else if (positionField['bottom-center-value']) {
                                newPositionValue = 'bottom-center-value';
                            } else if (positionField['bottom-right-value']) {
                                newPositionValue = 'bottom-right-value';
                            } else {
                                console.warn("No valid bottom position values found, cannot update position data.");
                                return;
                            }
                        } else
                        if (newPositionValue.startsWith('center')) {
                            if (positionField['center-left-value']) {
                                newPositionValue = 'center-left-value';
                            } else if (positionField['center-value']) {
                                newPositionValue = 'center-value';
                            } else if (positionField['center-right-value']) {
                                newPositionValue = 'center-right-value';
                            } else {
                                console.warn("No valid center position values found, cannot update position data.");
                                return;
                            }
                        } else {
                            console.warn("Unknown position value:", newPositionValue);
                            return;
                        }
                    }
                    // Update the position dataLink with the new value
                    updateCustomisationValue(node.customisationVars, positionDataLinkToUse, positionField[newPositionValue], positionDataIndexToUse, null, node);
                } else {
                    // Keep existing position value
                    // Find the positionField[prop] that matches the current position
                    for (const prop in positionField) {
                        if (prop.endsWith('-value') && positionField[prop] === newPositionValue) {
                            newPositionValue = prop;
                            break;
                        }
                    }
                }
            }
            


            /* if (positionField && positionField.dataLink) {
                // Calculate the position based on the dropped position
                if (rect.top - previewWindowRect.top < 50) {
                    // Top
                    if (rect.left - previewWindowRect.left < 50) {
                        newPositionValue = 'top-left-value';
                    } else if (rect.right - previewWindowRect.right < 50) {
                        newPositionValue = 'top-right-value';
                    } else {
                        newPositionValue = 'top-center-value';
                    }
                } else if (rect.bottom - previewWindowRect.bottom < 50) {
                    // Bottom
                    if (rect.left - previewWindowRect.left < 50) {
                        newPositionValue = 'bottom-left-value';
                    } else if (rect.right - previewWindowRect.right < 50) {
                        newPositionValue = 'bottom-right-value';
                    } else {
                        newPositionValue = 'bottom-center-value';
                    }
                } else {
                    // Middle
                    if (rect.left - previewWindowRect.left < 50) {
                        newPositionValue = 'center-left-value';
                    } else if (rect.right - previewWindowRect.right < 50) {
                        newPositionValue = 'center-right-value';
                    } else {
                        newPositionValue = 'center-value';
                    }
                }
                console.log("New position value:", newPositionValue);
                // Bear in mind that the value we have just determined may not exist! So we need to check that and snap to the closest one
                if (!positionField[newPositionValue]) {
                    console.warn(`Position field does not have a value for ${newPositionValue}, cannot update position data.`);
                    // We can try to find the closest position value
                    // There is a good chance we will have at least a top / center / bottom value
                    if (newPositionValue.startsWith('top')) {
                        if (positionField['top-left-value']) {
                            newPositionValue = 'top-left-value';
                        } else if (positionField['top-center-value']) {
                            newPositionValue = 'top-center-value';
                        } else if (positionField['top-right-value']) {
                            newPositionValue = 'top-right-value';
                        } else {
                            console.warn("No valid top position values found, cannot update position data.");
                            return;
                        }
                    } else
                    if (newPositionValue.startsWith('bottom')) {
                        if (positionField['bottom-left-value']) {
                            newPositionValue = 'bottom-left-value';
                        } else if (positionField['bottom-center-value']) {
                            newPositionValue = 'bottom-center-value';
                        } else if (positionField['bottom-right-value']) {
                            newPositionValue = 'bottom-right-value';
                        } else {
                            console.warn("No valid bottom position values found, cannot update position data.");
                            return;
                        }
                    } else
                    if (newPositionValue.startsWith('center')) {
                        if (positionField['center-left-value']) {
                            newPositionValue = 'center-left-value';
                        } else if (positionField['center-value']) {
                            newPositionValue = 'center-value';
                        } else if (positionField['center-right-value']) {
                            newPositionValue = 'center-right-value';
                        } else {
                            console.warn("No valid center position values found, cannot update position data.");
                            return;
                        }
                    } else {
                        console.warn("Unknown position value:", newPositionValue);
                        return;
                    }
                }
                // Update the position dataLink with the new value
                updateCustomisationValue(node.customisationVars, positionField.dataLink, positionField[newPositionValue]);
            } */
            // If we have offset fields, we can update those too
            console.log('Setting offsets, using scale: ', previewScaleRef.current ?? previewScale);
            const offsetScaleFactor = 1 / (node?.applyScale ?? 1);
            if (offsetXField && offsetXField.dataLink) {
                // Get the offset-x value based on the dropped position
                let offsetX = 0;
                if (node.depth === 1 || (dragAdjustsPosition && positionField && positionField.dataLink)) {
                    offsetX = rect.left - previewWindowRect.left; // Use the dragging offset
                    // This offset is currently correct if the node is positioned at the top-left corner of the preview window
                    if (newPositionValue && newPositionValue.includes('right')) {
                        // If the position is on the right, we need to adjust the offset
                        offsetX = -(previewWindowRect.right - rect.right); // rect.right - previewWindowRect.left; // Use the dragging offset
                    }
                    // With center values, we need to adjust the offset based on the width of the rect as the center is the middle of the rect
                    if (newPositionValue && (newPositionValue === 'bottom-center-value' || newPositionValue === 'top-center-value' || newPositionValue === 'center-value')) {
                        // If the position is in the center, we need to adjust the offset based on the width of the rect
                        offsetX = rectCenterX - previewWindowCenterX; // Use the dragging offset
                    }

                    // Offset needs to be adjusted based on the preview scale
                    offsetX = (offsetX * offsetScaleFactor) / (previewScaleRef.current ?? previewScale); // Adjust for the preview scale
                } else {
                    const dragXDistance = (e?.clientX) - (draggingStartPositionRef.current?.x ?? draggingStartPosition?.x);
                    offsetX = getCustomisationValue(node.customisationVars, offsetXDataLinkToUse, offsetXDataIndexToUse, null, node) + (dragXDistance * offsetScaleFactor) / (previewScaleRef.current ?? previewScale);
                }
                
                // Update the offset-x value in the customisationVars
                updateCustomisationValue(node.customisationVars, offsetXDataLinkToUse, Math.round(offsetX), offsetXDataIndexToUse, null, node);
            }
            if (offsetYField && offsetYField.dataLink) {
                // Get the offset-y value based on the dropped position
                let offsetY = 0;
                if (node.depth === 1 || (dragAdjustsPosition && positionField && positionField.dataLink)) {
                    offsetY = rect.top - previewWindowRect.top; // Use the dragging offset
                    // This offset is currently correct if the node is positioned at the top-left corner of the preview window
                    if (newPositionValue && newPositionValue.includes('bottom')) {
                        // If the position is on the bottom, we need to adjust the offset
                        offsetY = -(previewWindowRect.bottom - rect.bottom); // rect.bottom - previewWindowRect.top; // Use the dragging offset
                    }
                    // With center values, we need to adjust the offset based on the height of the rect as the center is the middle of the rect
                    if (newPositionValue && (newPositionValue === 'center-value' || newPositionValue === 'center-left-value' || newPositionValue === 'center-right-value')) {
                        // If the position is in the center, we need to adjust the offset based on the height of the rect
                        offsetY = rectCenterY - previewWindowCenterY; // Use the dragging offset
                    }

                    // Offset needs to be adjusted based on the preview scale
                    offsetY = (offsetY * offsetScaleFactor) / (previewScaleRef.current ?? previewScale); // Adjust for the preview scale
                } else {
                    const dragYDistance = (e?.clientY) - (draggingStartPositionRef.current?.y ?? draggingStartPosition?.y);
                    offsetY = getCustomisationValue(node.customisationVars, offsetYDataLinkToUse, offsetYDataIndexToUse, null, node) + (dragYDistance * offsetScaleFactor) / (previewScaleRef.current ?? previewScale);
                }
                
                // Update the offset-y value in the customisationVars
                updateCustomisationValue(node.customisationVars, offsetYDataLinkToUse, Math.round(offsetY), offsetYDataIndexToUse, null, node);
            }

            // invalidateHighlightedNodeStates(); // Invalidate the highlighted node states to force a re-render
        }
    }

    const adjustOffsetXBy = (node, dataIndex, adjustBy) => {
        // Adjust the offset-x value by the specified amount
        console.log("Adjusting offset-x by", adjustBy, "for node:", node, "at dataIndex:", dataIndex);
        // Find position module in the node.config.children
        let positionModule = null;
        if (node.config && node.config.children) {
            for (const child of node.config.children) {
                if (child.type === 'module' && child.module === 'position') {
                    positionModule = child;
                    break;
                }
            }
        }
        if (positionModule) {
            // Find the offset-x field in the position module, we only care about this if it has a dataLink
            let offsetXField = null;
            for (const field of positionModule.fields) {
                if (field.field?.includes('position-offset')) {
                    if (field['offset-x']?.dataLink) {
                        offsetXField = field['offset-x'];
                    }
                }
            }
            if (offsetXField && offsetXField.dataLink) {
                const currentOffsetX = getCustomisationValue(node.customisationVars, offsetXField.dataLink, dataIndex, null, node) || 0;
                const newOffsetX = currentOffsetX + adjustBy;
                updateCustomisationValue(node.customisationVars, offsetXField.dataLink, newOffsetX, dataIndex, null, node);
            }
        }
    }
    const adjustOffsetYBy = (node, dataIndex, adjustBy) => {
        // Adjust the offset-y value by the specified amount
        console.log("Adjusting offset-y by", adjustBy, "for node:", node, "at dataIndex:", dataIndex);
        // Find position module in the node.config.children
        let positionModule = null;
        if (node.config && node.config.children) {
            for (const child of node.config.children) {
                if (child.type === 'module' && child.module === 'position') {
                    positionModule = child;
                    break;
                }
            }
        }
        if (positionModule) {
            // Find the offset-y field in the position module, we only care about this if it has a dataLink
            let offsetYField = null;
            for (const field of positionModule.fields) {
                if (field.field?.includes('position-offset')) {
                    if (field['offset-y']?.dataLink) {
                        offsetYField = field['offset-y'];
                    }
                }
            }
            if (offsetYField && offsetYField.dataLink) {
                const currentOffsetY = getCustomisationValue(node.customisationVars, offsetYField.dataLink, dataIndex, null, node) || 0;
                const newOffsetY = currentOffsetY + adjustBy;
                updateCustomisationValue(node.customisationVars, offsetYField.dataLink, newOffsetY, dataIndex, null, node);
            }
        }
    }

    const updateVisualisedElementDimensionData = (node, newWidth, newHeight) => {
        // If the node has a dimensions module, we update the width and height dataLinks
        // We are going to update the dimensions based on the new width and height
        console.log("Updating visualised element dimension data for node:", node, "with newWidth:", newWidth, "newHeight:", newHeight);
        // Find dimensions module in the node.config.children
        let dimensionsModule = null;
        if (node.config && node.config.children) {
            for (const child of node.config.children) {
                if (child.type === 'module' && child.module === 'dimensions') {
                    dimensionsModule = child;
                    break;
                }
            }
        }
        console.log("Found dimensions module:", dimensionsModule);
        if (dimensionsModule) {
            // Find width and height fields in the dimensions module, we only care about these if they have a dataLink
            let widthField = null;
            let heightField = null;
            for (let i = 0; i < dimensionsModule.fields.length; i++) {
                const field = dimensionsModule.fields[i];
                if (field["dimensions-height"]) {
                    if (field["dimensions-height"].dataLink) {
                        heightField = field["dimensions-height"];
                    }
                }
                if (field["dimensions-width"]) {
                    if (field["dimensions-width"].dataLink) {
                        widthField = field["dimensions-width"];
                    }
                }
            }
            console.log("Found width field:", widthField, "and height field:", heightField);

            const visualisationComponentKey = node?.config?.visualisationConfig?.visualisationComponent !== '<Standard>' ? node?.config?.visualisationConfig?.visualisationComponent : null;
            const index = node?.dataIndex || 0;

            // If we have a width field, update the width dataLink
            if (widthField && widthField.dataLink) {
                WORKING_ON_NODE_DATA.fieldData = widthField;
                WORKING_ON_NODE_DATA.node = node;
                updateCustomisationValue(node.customisationVars, widthField.dataLink, newWidth, index, visualisationComponentKey, node, Math.round(startSizeRectRef.current.width / (previewScaleRef.current ?? previewScale)));
            }
            // If we have a height field, update the height dataLink
            if (heightField && heightField.dataLink) {
                WORKING_ON_NODE_DATA.fieldData = heightField;
                WORKING_ON_NODE_DATA.node = node;
                updateCustomisationValue(node.customisationVars, heightField.dataLink, newHeight, index, visualisationComponentKey, node, Math.round(startSizeRectRef.current.height / (previewScaleRef.current ?? previewScale)));
            }

            // invalidateHighlightedNodeStates(); // Invalidate the highlighted node states to force a re-render
        }
    }

    const visualisedNodeVarKey = props.visualisedNode?.topNode?.varKey ?? props.visualisedNode?.varKey ?? props.selectedFeatureNode?.varKey;
    const customRenderer = CUSTOM_COMPONENTS_LIST?.[visualisedNodeVarKey] ?? CUSTOM_COMPONENTS_LIST?.[visualisedNodeVarKey?.split('_')[0]];
    console.log("EditablePreview render, visualisedNodeVarKey:", props, visualisedNodeVarKey, "customRenderer:", customRenderer);

    const selectedFeatureNode = props.selectedFeatureNode;
    const renderedReversedSubNodes = selectedFeatureNode?.subNodes.slice().reverse();
    // console.log("Rendered reversed sub-nodes:", selectedFeatureNode, renderedReversedSubNodes);

    return (
        <div id="previewWindowContainer" className='v2-preview-window-holder'>

            <div id="previewWindow" className={`v2-preview preview-bg-${previewBg}`} 
                onClick={
                    () => {
                        if (!editingTextContentData && props.visualisedNode) {
                            props.activateNodeFunction(props.visualisedNode)
                        }
                    }
                }
            >
                {//!draggingNode &&
                <>
                    {customRenderer &&
                        <>
                            {customRenderer.render(
                                {
                                    node: props?.visualisedNode ?? props.selectedFeatureNode,
                                    activeNode: props?.activeNode ?? props.selectedFeatureNode,
                                    focusEditingOnNode: props?.focusEditingOnNode,
                                    setFocusEditingOnNode: props?.setFocusEditingOnNode,
                                    visualisedNode: props?.visualisedNode ?? props.selectedFeatureNode,
                                    timelinePlayheadStep: props.timelinePlayheadStep,
                                    timelinePlayheadLabel: props.timelinePlayheadLabel,
                                    activateNodeFunction: props.activateNodeFunction,
                                    editingTextContentData: editingTextContentData,
                                    setEditingTextContentData: setEditingTextContentData,
                                    highlightHoveredNode: highlightHoveredNode,
                                    unhighlightHoveredNode: unhighlightHoveredNode,
                                    startDraggingNode: startDraggingNode,
                                    draggingActive: draggingActive,
                                    draggingCancelled: draggingCancelled,
                                    draggingNode: draggingNode,
                                    contentClicked: contentClicked,
                                    invalidateHighlightedNodeStates: invalidateHighlightedNodeStates,
                                    previewScale: previewScale,
                                    imageMode: IMAGE_MODES.fillArea,
                                    visualisedNodeVarKey: visualisedNodeVarKey,
                                    campaignData: campaignData,
                                    forceUpdateModuleListFunc: props.forceUpdateModuleListFunc,
                                    forceUpdateComponent: props.forceUpdateComponent,
                                }
                            )}
                        </>
                    }
                    {/*!customRenderer && props.visualisedNode &&
                        <VisualisedNode 
                            node={props.visualisedNode} 
                            activeNode={props.activeNode} 
                            activateNodeFunction={props.activateNodeFunction} 
                            topLevel={true} 
                            editingTextContentData={editingTextContentData}
                            setEditingTextContentData={setEditingTextContentData}
                            highlightHoveredNode={highlightHoveredNode}
                            unhighlightHoveredNode={unhighlightHoveredNode}
                            startDraggingNode={startDraggingNode}
                            draggingActive={draggingActive}
                            draggingCancelled={draggingCancelled}
                            draggingNode={draggingNode}
                            contentClicked={contentClicked}
                            invalidateHighlightedNodeStates={invalidateHighlightedNodeStates}
                            previewScale={previewScale}
                            imageMode={IMAGE_MODES.fillArea}
                            campaignData={campaignData}
                        />
                    */}
                    {!customRenderer && renderedReversedSubNodes && renderedReversedSubNodes.length &&
                        renderedReversedSubNodes.map((node, index) => {
                            const nodeStartStep = node?.config?.visualisationConfig?.startStep ?? -1;
                            const nodeEndStep = node?.config?.visualisationConfig?.endStep ?? -1;
                            if (nodeStartStep > props.timelinePlayheadStep || nodeEndStep < props.timelinePlayheadStep) {
                                return null;
                            }
                            return (
                                <Fragment key={node.id + '_' + index}>
                                {/* <div className={`v2-top-level-node-container${node !== props.visualisedNode ? ' v2-no-mouse-events' : ''}`} key={'vNode_' + index}>
                                    <div className='rel'> */}
                                        <VisualisedNode 
                                            node={node} 
                                            visualisedNode={props.visualisedNode}
                                            activeNode={props.activeNode} 
                                            focusEditingOnNode={props?.focusEditingOnNode}
                                            setFocusEditingOnNode={props?.setFocusEditingOnNode}
                                            activateNodeFunction={props.activateNodeFunction} 
                                            timelinePlayheadStep={props.timelinePlayheadStep}
                                            timelinePlayheadLabel={props.timelinePlayheadLabel}
                                            topLevel={true} 
                                            editingTextContentData={editingTextContentData}
                                            setEditingTextContentData={setEditingTextContentData}
                                            highlightHoveredNode={highlightHoveredNode}
                                            unhighlightHoveredNode={unhighlightHoveredNode}
                                            startDraggingNode={startDraggingNode}
                                            draggingActive={draggingActive}
                                            draggingCancelled={draggingCancelled}
                                            draggingNode={draggingNode}
                                            contentClicked={contentClicked}
                                            invalidateHighlightedNodeStates={invalidateHighlightedNodeStates}
                                            forceUpdateModuleListFunc={props.forceUpdateModuleListFunc}
                                            forceUpdateComponent={props.forceUpdateComponent}
                                            previewScale={previewScale}
                                            imageMode={props.visualisedNode?.config?.visualisationConfig?.isFullScreen ? IMAGE_MODES.fillArea : IMAGE_MODES.naturalSize}
                                            campaignData={campaignData}
                                            // index={node.subNodes.indexOf(props?.activeNode) >= 0 && props?.activeNode?.dataIndex ? props.activeNode.dataIndex : 0}
                                        />
                                    {/* </div>
                                </div> */}
                                </Fragment>
                            );
                        })
                    }
                </>
                }
                {!editingTextContentData && highlightedDomNodeRect && !draggingNode &&
                    <>
                        {/* console.log("Highlighted DOM Node Rect:", highlightedDomNodeRect, highlightedNodeDomElement) */}
                        <div id="boxHighlight" className='v2-highlighted-dom-element' style={{left: `${highlightedDomNodeRect.left}px`, top: `${highlightedDomNodeRect.top}px`, width: `${highlightedDomNodeRect.width}px`, height: `${highlightedDomNodeRect.height}px`}}></div>
                        {highlightedNodeIsResizable &&
                            <>
                                {highlightedNodeIsWidthScaleable && highlightedNodeIsHeightScaleable &&
                                    <div id="nw" className='v2-resize-handle v2-nwse-resize-handle' style={{left: `${highlightedDomNodeRect.left}px`, top: `${highlightedDomNodeRect.top}px`}} onMouseDown={startDraggingResizeHandle}></div>
                                }
                                {highlightedNodeIsHeightScaleable && !forceProportionalScaling &&
                                    <div id="n" className='v2-resize-handle v2-ns-resize-handle' style={{left: `${highlightedDomNodeRect.left + highlightedDomNodeRect.width / 2}px`, top: `${highlightedDomNodeRect.top}px`}} onMouseDown={startDraggingResizeHandle}></div>
                                }
                                {highlightedNodeIsWidthScaleable && highlightedNodeIsHeightScaleable &&
                                    <div id="ne" className='v2-resize-handle v2-nesw-resize-handle' style={{left: `${highlightedDomNodeRect.left + highlightedDomNodeRect.width}px`, top: `${highlightedDomNodeRect.top}px`}} onMouseDown={startDraggingResizeHandle}></div>
                                }
                                {highlightedNodeIsWidthScaleable && !forceProportionalScaling &&
                                    <div id="e" className='v2-resize-handle v2-ew-resize-handle' style={{left: `${highlightedDomNodeRect.left + highlightedDomNodeRect.width}px`, top: `${highlightedDomNodeRect.top + highlightedDomNodeRect.height / 2}px`}} onMouseDown={startDraggingResizeHandle}></div>
                                }
                                {highlightedNodeIsWidthScaleable && highlightedNodeIsHeightScaleable &&
                                    <div id="sw" className='v2-resize-handle v2-nesw-resize-handle' style={{left: `${highlightedDomNodeRect.left}px`, top: `${highlightedDomNodeRect.top + highlightedDomNodeRect.height}px`}} onMouseDown={startDraggingResizeHandle}></div>
                                }
                                {highlightedNodeIsHeightScaleable && !forceProportionalScaling &&
                                    <div id="s" className='v2-resize-handle v2-ns-resize-handle' style={{left: `${highlightedDomNodeRect.left + highlightedDomNodeRect.width / 2}px`, top: `${highlightedDomNodeRect.top + highlightedDomNodeRect.height}px`}} onMouseDown={startDraggingResizeHandle}></div>
                                }
                                {highlightedNodeIsWidthScaleable && highlightedNodeIsHeightScaleable &&
                                    <div id="se" className='v2-resize-handle v2-nwse-resize-handle' style={{left: `${highlightedDomNodeRect.left + highlightedDomNodeRect.width}px`, top: `${highlightedDomNodeRect.top + highlightedDomNodeRect.height}px`}} onMouseDown={startDraggingResizeHandle}></div>
                                }
                                {highlightedNodeIsWidthScaleable && !forceProportionalScaling &&
                                    <div id="w" className='v2-resize-handle v2-ew-resize-handle' style={{left: `${highlightedDomNodeRect.left}px`, top: `${highlightedDomNodeRect.top + highlightedDomNodeRect.height / 2}px`}} onMouseDown={startDraggingResizeHandle}></div>
                                }
                            </>
                        }
                    </>
                }
                {!editingTextContentData && hoverHighlightedDomNodeRect && !draggingNode &&
                    <>
                        <div id="hoverHighlight" className='v2-hover-highlighted-dom-element' style={{left: `${hoverHighlightedDomNodeRect.left}px`, top: `${hoverHighlightedDomNodeRect.top}px`, width: `${hoverHighlightedDomNodeRect.width}px`, height: `${hoverHighlightedDomNodeRect.height}px`}}></div>
                    </>
                }
            </div>

        </div>
    )
}

export default EditablePreview;
